// AS PÁGINAS INSTITUCIONAIS, que antes eram âncora da home.
//
// O pedido, em dois pontos do relatório:
//
//   T1.18 — "Home, 'quem somos', 'objetivos' e 'parceria' são seções da mesma página via âncora,
//           não páginas próprias — ruim de usar."
//   T1.36 — "Quem somos, objetivos e parceiros estão todos juntos na mesma página. Sugestão:
//           Quem somos + Parceiros juntos e o Objetivo como algo extra e mais interativo."
//
// O que isto resolve, além da queixa: com rota própria, cada seção passa a ter endereço que se
// compartilha, título de aba próprio e volta do botão "voltar" do navegador. Com âncora, os três
// eram a mesma URL.
//
// O TEXTO NÃO FOI REESCRITO. Ele é o mesmo de `landing-page.tsx` — separar em páginas é mudança
// de navegação, não de conteúdo, e reescrever a fala institucional da UNEB não é decisão minha.
// A home passa a mostrar a ferramenta em primeiro plano (T1.11) e linkar para cá.

import { PARCEIROS } from "./os-parceiros";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../../components/nav";
import "./institucional.scss";

export type SecaoInstitucional = "quem-somos" | "objetivos" | "parceiros";

const TITULOS: Record<SecaoInstitucional, string> = {
  "quem-somos": "Quem somos",
  objetivos: "Objetivos",
  parceiros: "Parceiros",
};

function Institucional({ secao }: { secao: SecaoInstitucional }) {
  const navigate = useNavigate();

  useEffect(() => {
    // Título de aba próprio — uma das coisas que a âncora não dava.
    document.title = `${TITULOS[secao]} · HQ-Lab`;
    return () => {
      document.title = "HQ-Lab";
    };
  }, [secao]);

  return (
    <div className="institucional-container">
      <Nav />

      <header className="institucional-topo hq-trama">
        <div className="institucional-largura">
          <h1>{TITULOS[secao]}</h1>
        </div>
      </header>

      <main className="institucional-conteudo institucional-largura">
        {secao === "quem-somos" && <QuemSomos />}
        {secao === "objetivos" && <Objetivos />}
        {secao === "parceiros" && <Parceiros />}

        <nav className="institucional-navegacao" aria-label="Outras páginas">
          {(Object.keys(TITULOS) as SecaoInstitucional[])
            .filter((s) => s !== secao)
            .map((s) => (
              <button key={s} type="button" onClick={() => navigate(`/${s}`)}>
                {TITULOS[s]}
              </button>
            ))}
        </nav>
      </main>
    </div>
  );
}

function QuemSomos() {
  return (
    <article className="institucional-texto">
      <p>
        O HQ-Lab nasce no Grupo de Pesquisa Geotecnologias, Educação e Contemporaneidade,
        carinhosamente chamado de GEOTEC, cadastrado no CNPQ pela Universidade
        do Estado da Bahia (UNEB).
      </p>
      <p>
        O grupo reúne pesquisadores do Programa de Pós-Graduação em Educação e Contemporaneidade
        (PPGEDUC) e do mestrado em Gestão e Tecnologias Aplicadas à Educação (GESTEC), em parceria com
        escolas municipais, estaduais e a comunidade.
      </p>
      <p>
        Somos gente da computação, do design e da publicidade, entre outras áreas, e buscamos
        mudar a forma como a tecnologia chega à sala de aula.
      </p>
    </article>
  );
}

/**
 * Objetivos como uma sequência numerada.
 *
 * O T1.36 pede o objetivo "como algo extra e mais interativo, mostrar em quadrinhos: um → tal
 * coisa; dois → tal coisa". A numeração em quadros é a forma dessa sugestão que dá para entregar
 * sem inventar conteúdo novo — o texto continua sendo o que a home já dizia.
 */
function Objetivos() {
  const objetivos = [
    {
      titulo: "Levar a criação para dentro da aula",
      texto:
        "Uma ferramenta de quadrinhos que a professora usa na aula, sem instalar nada e sem " +
        "precisar saber desenhar.",
    },
    {
      titulo: "Aproximar comunidade, universidade e escola",
      texto:
        "Aproximar a comunidade, a universidade e a escola, contribuindo para a melhoria da " +
        "qualidade educacional no Estado da Bahia.",
    },
    {
      titulo: "Ampliar o repertório digital de quem ensina",
      texto:
        "Contribuir com a formação de professores para as tecnologias digitais e incentivar a " +
        "criatividade de estudantes e educadores.",
    },
    {
      titulo: "Mostrar o que a tecnologia pode na educação",
      texto:
        "Difundir as potencialidades das tecnologias na educação contemporânea, com material " +
        "feito pelas próprias turmas.",
    },
  ];

  return (
    <ol className="institucional-objetivos">
      {objetivos.map((o, i) => (
        <li key={o.titulo}>
          <span className="institucional-numero">{String(i + 1).padStart(2, "0")}</span>
          <span>
            <strong>{o.titulo}</strong>
            <span>{o.texto}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Parceiros.
 *
 * O T1.19 mediu o problema: "excesso de texto e poucas logos". Na home eram 49 palavras para 1
 * logo. Aqui a proporção se inverte — a página é das marcas, com uma linha de contexto.
 *
 * As logos entram como espaço reservado nomeado, e não como imagem inventada: os arquivos das
 * marcas parceiras não estão no repositório, e desenhar um logo falso seria pior que a lacuna.
 */
/**
 * PARCEIROS.
 *
 * T1.19 do pedido: "Nossas parceiras — muito texto, pouca imagem". Quando eu finalmente olhei a
 * página, ela estava no extremo OPOSTO: uma frase e quatro retângulos vazios de contorno
 * tracejado. Uma passada anterior corrigiu demais — tirou a parede de texto e não pôs nada no
 * lugar.
 *
 * Pior: o cabeçalho deste arquivo afirma "O TEXTO NÃO FOI REESCRITO, ele é o mesmo de
 * `landing-page.tsx`". Para esta seção isso tinha deixado de ser verdade. O texto institucional
 * do operador sobre a UNEB e a MVP simplesmente sumiu.
 *
 * Ele está de volta, VERBATIM da versão clássica. Não reescrevi uma vírgula: fala institucional
 * sobre financiamento público e sobre uma empresa real não é texto que eu edite por conta
 * própria, e o edital citado é um número que ou está certo ou está errado.
 *
 * O que muda em relação ao original é a FORMA, que é o que o ponto pede: cada parceiro é um bloco
 * com o nome como título, em vez de parágrafos soltos empilhados. Texto que se lê por partes deixa
 * de ser parede.
 *
 * E os parceiros sem texto continuam sem texto. Eu não tenho o que a GEOTEC, o PPGEDUC e as
 * escolas fizeram no projeto, e inventar descrição de instituição real seria a pior coisa que esta
 * página poderia fazer.
 */
function Parceiros() {
  // 06/10/2026 · só os parceiros oficiais que têm marca (decisão do operador). A nota do fim, que
  // declarava "as marcas ainda estão sendo reunidas", saiu: todo parceiro desta página tem a dele,
  // e uma nota que fala de lacuna fechada só mente devagar.
  return (
    <>
      <p className="institucional-texto">Os parceiros oficiais do HQ-Lab.</p>

      <div className="institucional-parceiro-blocos">
        {PARCEIROS.map((p) => (
          <article key={p.nome} className="institucional-parceiro">
            <img className="institucional-parceiro-logo" src={p.logo} alt={`Marca ${p.nome}`} />
            <h2>{p.nome}</h2>
            {p.completo && <p className="institucional-parceiro-papel">{p.completo}</p>}
          </article>
        ))}
      </div>
    </>
  );
}

export default Institucional;
